/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {AdornmentType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/AdornmentType";
import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QTableVariant} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableVariant";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import {GridColDef, GridColType, GridColumnHeaderParams, GridRowsProp, MuiEvent} from "@mui/x-data-grid-pro";
import HelpContent, {hasHelpContent} from "qqq/components/misc/HelpContent";
import ValueUtils from "qqq/utils/qqq/ValueUtils";
import MultiLineCell from "qqq/components/query/MultiLineCell";
import MultiLineColumnHeader from "qqq/components/query/MultiLineColumnHeader";
import {getQueryScreenMultiLineCellSeparator} from "qqq/utils/qqq/QueryScreenFieldHints";
import React from "react";
import {Link, NavigateFunction} from "react-router-dom";


export default class DataGridUtils
{
   /*******************************************************************************
    **
    *******************************************************************************/
   public static handleRowClick = (path: string, event: MuiEvent<React.MouseEvent>, gridMouseDownX: number, gridMouseDownY: number, navigate: NavigateFunction, instance: any) =>
   {
      ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // strategy for when to trigger or not trigger a row click:                                                      //
      // To avoid a drag-event that highlighted text in a cell:                                                        //
      // - we capture the x & y upon mouse-down - then compare them in this method (which fires when the mouse is up)  //
      //   if they are more than 5 pixels away from the mouse-down, then assume it's a drag, not a click.              //
      // - avoid clicking the row upon double-click, by setting a 500ms timer here - and in the onDoubleClick handler, //
      //   cancelling the timer.                                                                                       //
      // - also avoid a click, then click-again-and-start-dragging, by always cancelling the timer in mouse-down.      //
      // All in, these seem to have good results - the only downside being the half-second delay...                    //
      ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      const diff = Math.max(Math.abs(event.clientX - gridMouseDownX), Math.abs(event.clientY - gridMouseDownY));
      if (diff < 5)
      {
         console.log("clearing timeout");
         clearTimeout(instance.current.timer);
         instance.current.timer = setTimeout(() =>
         {
            navigate(path);
         }, 100);
      }
      else
      {
         console.log(`row-click mouse-up happened ${diff} x or y pixels away from the mouse-down - so not considering it a click.`);
      }
   };

   /*******************************************************************************
    ** A primitive a grid can sort on for one field of one record.  Possible-value
    ** fields sort by their display label (what the user sees — a carrier service
    ** column orders AxleHire first, not by its numeric id); other numbers /
    ** booleans / dates sort by raw value (ISO strings order correctly); anything
    ** else by display value, falling back to raw.
    *******************************************************************************/
   private static makeSortValue(field: QFieldMetaData, record: QRecord, fieldName: string): any
   {
      const rawValue = record.values.get(fieldName);
      const displayValue = record.displayValues.get(fieldName);

      if (field.possibleValueSourceName)
      {
         return (displayValue !== undefined && displayValue !== null ? displayValue : rawValue);
      }

      if (field.type == QFieldType.INTEGER || field.type == QFieldType.DECIMAL || field.type == QFieldType.BOOLEAN
         || field.type == QFieldType.DATE || field.type == QFieldType.DATE_TIME || field.type == QFieldType.TIME)
      {
         return (rawValue);
      }

      return (displayValue !== undefined && displayValue !== null ? displayValue : rawValue);
   }



   /*******************************************************************************
    ** Comparator for client-side grid sorting that orders by row.__sortValues
    ** primitives (see makeSortValue) rather than cell values, which may be React
    ** elements.  Nulls sort last (per sort direction); mixed types compare as
    ** strings.  Falls back to the cell values when __sortValues isn't present
    ** (rows not built by makeRows).
    *******************************************************************************/
   public static sortValuesComparator(fieldName: string): (v1: any, v2: any, cellParams1: any, cellParams2: any) => number
   {
      return (v1: any, v2: any, cellParams1: any, cellParams2: any): number =>
      {
         const s1 = cellParams1?.api?.getRow?.(cellParams1.id)?.__sortValues?.[fieldName] ?? v1;
         const s2 = cellParams2?.api?.getRow?.(cellParams2.id)?.__sortValues?.[fieldName] ?? v2;

         const isNull1 = (s1 === null || s1 === undefined || s1 === "");
         const isNull2 = (s2 === null || s2 === undefined || s2 === "");
         if (isNull1 && isNull2)
         {
            return (0);
         }
         if (isNull1)
         {
            return (-1);
         }
         if (isNull2)
         {
            return (1);
         }

         if (typeof s1 == "number" && typeof s2 == "number")
         {
            return (s1 - s2);
         }
         if (typeof s1 == "boolean" && typeof s2 == "boolean")
         {
            return ((s1 ? 1 : 0) - (s2 ? 1 : 0));
         }
         return (String(s1).localeCompare(String(s2), undefined, {numeric: true, sensitivity: "base"}));
      };
   }



   public static makeRows = (results: QRecord[], tableMetaData: QTableMetaData, tableVariant?: QTableVariant, allowEmptyId = false, maxValueLength: number = 2048): GridRowsProp[] =>
   {
      function trimValue(value: any, maxLength: number)
      {
         if(typeof value == "string" && maxLength)
         {
            if(value.length > maxLength)
            {
               return (value.substring(0, maxLength) + "...");
            }
         }

         return value;
      }

      ///////////////////////////////////////////////////////////////////////////////////////////
      // a multi-line cell shows the whole list when expanded, so it is not trimmed: a trimmed //
      // list would expand with its last entries silently missing                              //
      ///////////////////////////////////////////////////////////////////////////////////////////
      function maxValueLengthFor(field: QFieldMetaData): number
      {
         return (getQueryScreenMultiLineCellSeparator(field) ? 0 : maxValueLength);
      }

      const fields = [...tableMetaData.fields.values(), ...(tableMetaData.virtualFields?.values() ?? [])] as QFieldMetaData[];
      const rows = [] as any[];
      let rowIndex = 0;
      results.forEach((record: QRecord) =>
      {
         const row: any = {};
         row.__rowIndex = rowIndex++;

         /////////////////////////////////////////////////////////////////////////////////////////////
         // cell values below may be React elements (adorned fields), which client-side sorting     //
         // can't compare — so also stash a sortable primitive per field (see sortValuesComparator) //
         /////////////////////////////////////////////////////////////////////////////////////////////
         row.__sortValues = {};

         fields.forEach((field) =>
         {
            row[field.name] = trimValue(ValueUtils.getDisplayValue(field, record, "query", undefined, tableVariant), maxValueLengthFor(field));
            row.__sortValues[field.name] = DataGridUtils.makeSortValue(field, record, field.name);
         });

         if (tableMetaData.exposedJoins)
         {
            for (let i = 0; i < tableMetaData.exposedJoins.length; i++)
            {
               const join = tableMetaData.exposedJoins[i];

               if (join?.joinTable?.fields?.values())
               {
                  const fields = [...join.joinTable.fields.values(), ...(join.joinTable.virtualFields?.values() ?? [])];
                  fields.forEach((field) =>
                  {
                     let fieldName = join.joinTable.name + "." + field.name;
                     row[fieldName] = trimValue(ValueUtils.getDisplayValue(field, record, "query", fieldName, tableVariant), maxValueLengthFor(field));
                     row.__sortValues[fieldName] = DataGridUtils.makeSortValue(field, record, fieldName);
                  });
               }
            }
         }

         if (!row["id"])
         {
            row["id"] = record.values.get(tableMetaData.primaryKeyField) ?? row[tableMetaData.primaryKeyField];
            if (row["id"] === null || row["id"] === undefined)
            {
               /////////////////////////////////////////////////////////////////////////////////////////
               // DataGrid gets very upset about a null or undefined here, so, try to make it happier //
               /////////////////////////////////////////////////////////////////////////////////////////
               if (!allowEmptyId)
               {
                  row["id"] = "--";
               }
            }
         }

         rows.push(row);
      });

      return (rows);
   };

   /*******************************************************************************
    **
    *******************************************************************************/
   public static setupGridColumns = (tableMetaData: QTableMetaData, linkBase: string = "", metaData?: QInstance, columnSort: "bySection" | "alphabetical" = "alphabetical", includeExposedJoinTables: string[] = undefined, omitLinks: boolean = false): GridColDef[] =>
   {
      ////////////////////////////////////////////////////////////////////////////////////////
      // omitLinks is for modal usages of the grid (e.g., the record-picker) - where a link //
      // in any cell (main-table pk or an exposed-join table's pk) would navigate the page  //
      // underneath the modal, unmounting it - so blank the main-table linkBase, and skip   //
      // building the join-table link bases below, so that NO column renders links.         //
      ////////////////////////////////////////////////////////////////////////////////////////
      if (omitLinks)
      {
         linkBase = null;
      }

      const columns = [] as GridColDef[];
      this.addColumnsForTable(tableMetaData, linkBase, columns, columnSort, null, null);

      if (metaData)
      {
         if (tableMetaData.exposedJoins)
         {
            for (let i = 0; i < tableMetaData.exposedJoins.length; i++)
            {
               const join = tableMetaData.exposedJoins[i];
               let joinTableName = join.joinTable.name;
               if (metaData.tables.has(joinTableName) && metaData.tables.get(joinTableName).readPermission && (includeExposedJoinTables === undefined || includeExposedJoinTables.indexOf(joinTableName) > -1))
               {
                  let joinLinkBase = null;
                  if (!omitLinks)
                  {
                     joinLinkBase = metaData.getTablePath(join.joinTable);
                     if (joinLinkBase)
                     {
                        joinLinkBase += joinLinkBase.endsWith("/") ? "" : "/";
                     }
                  }

                  if (join?.joinTable?.fields?.values())
                  {
                     this.addColumnsForTable(join.joinTable, joinLinkBase, columns, columnSort, joinTableName + ".", join.label + ": ");
                  }
               }
            }
         }
      }

      return (columns);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   private static addColumnsForTable(tableMetaData: QTableMetaData, linkBase: string, columns: GridColDef[], columnSort: "bySection" | "alphabetical" = "alphabetical", namePrefix?: string, labelPrefix?: string)
   {
      const sortedKeys: string[] = [];

      ////////////////////////////////////////////////////////////////////////
      // this sorted by sections - e.g., manual sorting by the meta-data... //
      ////////////////////////////////////////////////////////////////////////
      if (columnSort === "bySection")
      {
         ////////////////////////////////////////
         // build set of virtual fields to use //
         ////////////////////////////////////////
         const virtualFieldsToUse: Record<string, true> = {};
         (tableMetaData.virtualFields ?? []).forEach((virtualField) =>
         {
            if (virtualField.capabilities.has(Capability.FIELD_SELECTABLE))
            {
               virtualFieldsToUse[virtualField.name] = true;
            }
         });

         /////////////////////////////////////////////////////////////////////////////////////////
         // loop over sections, adding all fields from sections first, then, if there's         //
         // an alternative section, add any virtual fields which are in our set one ones to use //
         // this doesn't quite put exactly where they'd be, but it's an okay first version.     //
         /////////////////////////////////////////////////////////////////////////////////////////
         for (let i = 0; i < tableMetaData.sections.length; i++)
         {
            const section = tableMetaData.sections[i];
            if (!section.fieldNames)
            {
               continue;
            }

            for (let j = 0; j < section.fieldNames.length; j++)
            {
               sortedKeys.push(section.fieldNames[j]);
            }

            if (section.alternatives?.has("RECORD_VIEW"))
            {
               section.alternatives.get("RECORD_VIEW").fieldNames.forEach((fieldName) =>
               {
                  if(virtualFieldsToUse[fieldName])
                  {
                     sortedKeys.push(fieldName);
                     delete virtualFieldsToUse[fieldName];
                  }
               })
            }
         }

         //////////////////////////////////////////////////////////////
         // if there are any leftover virtuals, add them to the end. //
         //////////////////////////////////////////////////////////////
         for (let virtualFieldName in virtualFieldsToUse)
         {
            sortedKeys.push(virtualFieldName);
         }

      }
      else // columnSort = "alphabetical"
      {
         ///////////////////////////
         // sort by labels... mmm //
         ///////////////////////////
         sortedKeys.push(...tableMetaData.fields.keys());

         if(tableMetaData.virtualFields)
         {
            for (let virtualField of tableMetaData.virtualFields.values())
            {
               if(virtualField.capabilities.has(Capability.FIELD_SELECTABLE))
               {
                  sortedKeys.push(virtualField.name);
               }
            }
         }

         sortedKeys.sort((a: string, b: string): number =>
         {
            const fieldA = tableMetaData.fields.get(a) ?? tableMetaData.virtualFields?.get(a);
            const fieldB = tableMetaData.fields.get(b) ?? tableMetaData.virtualFields?.get(b);

            const labelA = fieldA?.label ?? "";
            const labelB = fieldB?.label ?? "";

            return (labelA.localeCompare(labelB));
         });
      }

      sortedKeys.forEach((key) =>
      {
         const field = tableMetaData.fields.get(key) ?? tableMetaData.virtualFields?.get(key);
         if (!field)
         {
            return;
         }
         ////////////////////////////////////////////////////////////////////////////////
         // only apply the FIELD_SELECTABLE filter when the field actually declares    //
         // capabilities.  synthetic fields built client-side (e.g., the count/percent //
         // columns in the column-stats grid) carry an empty capabilities set and must //
         // still render.                                                              //
         ////////////////////////////////////////////////////////////////////////////////
         if (field.capabilities?.size && !field.capabilities.has(Capability.FIELD_SELECTABLE))
         {
            return;
         }
         if (field.isHeavy)
         {
            if (field.type == QFieldType.BLOB)
            {
               ////////////////////////////////////////////////////////
               // assume we DO want heavy blobs - as download links. //
               ////////////////////////////////////////////////////////
            }
            else
            {
               ///////////////////////////////////////////////////
               // otherwise, skip heavy fields on query screen. //
               ///////////////////////////////////////////////////
               return;
            }
         }

         const column = this.makeColumnFromField(field, tableMetaData, namePrefix, labelPrefix);
         column.sortable = field.capabilities?.has(Capability.FIELD_ORDER_BY) ?? true;
         column.filterable = field.capabilities?.has(Capability.FIELD_FILTER_CRITERIA) ?? true;
         column.groupable = field.capabilities?.has(Capability.FIELD_GROUP_BY) ?? true;

         ///////////////////////////////////////////////////////////////////////////////////////////////
         // cell VALUES from makeRows may be React elements (link/chip-adorned fields, PVS links), so //
         // client-side sorting (child-record-list grids — the query screen sorts server-side) can't  //
         // compare them.  sort on the primitive stashed in row.__sortValues instead; fall back to    //
         // the cell value for rows not built by makeRows.                                            //
         ///////////////////////////////////////////////////////////////////////////////////////////////
         column.sortComparator = DataGridUtils.sortValuesComparator(column.field);

         if (key === tableMetaData.primaryKeyField && linkBase && namePrefix == null)
         {
            columns.splice(0, 0, column);
         }
         else
         {
            columns.push(column);
         }

         if (key === tableMetaData.primaryKeyField && linkBase)
         {
            column.renderCell = (cellValues: any) => (
               cellValues.value ? <Link to={`${linkBase}${encodeURIComponent(cellValues.value)}`} onClick={(e) => e.stopPropagation()}>{cellValues.value}</Link> : ""
            );
         }
      });
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   public static makeColumnFromField = (field: QFieldMetaData, tableMetaData: QTableMetaData, namePrefix?: string, labelPrefix?: string): GridColDef =>
   {
      let columnType: GridColType = "string";

      if (field.possibleValueSourceName)
      {
         ///////////////
         // noop here //
         ///////////////
      }
      else
      {
         switch (field.type)
         {
            case QFieldType.DECIMAL:
            case QFieldType.INTEGER:
               columnType = "number";
               break;
            case QFieldType.DATE:
            case QFieldType.DATE_TIME:
               ///////////////////////////////////////////////////////////////////////////////////////////
               // as of MUI X v6+, "date" & "dateTime" column types require row values to be actual    //
               // Date objects (they throw otherwise) - but our rows hold pre-formatted display        //
               // strings (and we render via renderCell, with server-side sort & filter) - so just     //
               // treat these as strings, which is how they rendered under v5 anyway.                  //
               ///////////////////////////////////////////////////////////////////////////////////////////
               columnType = "string";
               break;
            case QFieldType.BOOLEAN:
               columnType = "string"; // using boolean gives an odd 'no' for nulls.
               break;
            case QFieldType.BLOB:
               break;
            default:
            ////////////////////////////
            // noop - leave as string //
            ////////////////////////////
         }
      }

      let headerName = labelPrefix ? labelPrefix + field.label : field.label;
      let fieldName = namePrefix ? namePrefix + field.name : field.name;

      const column: GridColDef = {
         field: fieldName,
         type: columnType,
         headerName: headerName,
         width: DataGridUtils.getColumnWidthForField(field, tableMetaData),
         renderCell: null as any,
      };

      column.renderCell = (cellValues: any) => (
         (cellValues.value)
      );

      const multiLineCellSeparator = getQueryScreenMultiLineCellSeparator(field);
      if (multiLineCellSeparator)
      {
         column.renderCell = (cellValues: any) => (
            <MultiLineCell value={cellValues.value} rowId={cellValues.id} separator={multiLineCellSeparator} />
         );
      }

      const helpRoles = ["QUERY_SCREEN", "READ_SCREENS", "ALL_SCREENS"];
      const showHelp = hasHelpContent(field.helpContents, helpRoles); // todo - maybe - take helpHelpActive from context all the way down to here?
      let headerTitle: JSX.Element = null;
      if (showHelp)
      {
         const formattedHelpContent = <HelpContent helpContents={field.helpContents} roles={helpRoles} heading={headerName} helpContentKey={`table:${tableMetaData.name};field:${fieldName}`} />;
         headerTitle = (
            <Tooltip title={formattedHelpContent}>
               <div className="MuiDataGrid-columnHeaderTitle" style={{lineHeight: "initial"}}>
                  {headerName}
               </div>
            </Tooltip>
         );
         column.renderHeader = (params: GridColumnHeaderParams) => headerTitle;
      }

      //////////////////////////////////////////////////////////////////////////////////
      // a multi-line column's header carries the same expand/collapse control as its //
      // cells, acting on every row at once (keeping the help tooltip, if any)        //
      //////////////////////////////////////////////////////////////////////////////////
      if (multiLineCellSeparator)
      {
         column.headerClassName = "qqq-multi-line-column-header";
         column.renderHeader = (params: GridColumnHeaderParams) => (
            <MultiLineColumnHeader title={headerTitle ?? <div className="MuiDataGrid-columnHeaderTitle">{headerName}</div>} field={fieldName} separator={multiLineCellSeparator} />
         );
      }

      return (column);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   public static getColumnWidthForField = (field: QFieldMetaData, table?: QTableMetaData): number =>
   {
      if (field.hasAdornment(AdornmentType.SIZE))
      {
         const sizeAdornment = field.getAdornment(AdornmentType.SIZE);
         const width: string = sizeAdornment.getValue("width");
         const widths: Map<string, number> = new Map<string, number>([
            ["small", 100],
            ["medium", 200],
            ["medlarge", 300],
            ["large", 400],
            ["xlarge", 600]
         ]);
         if (widths.has(width))
         {
            return widths.get(width);
         }
         else
         {
            console.log("Unrecognized size.width adornment value: " + width);
         }
      }

      if (field.possibleValueSourceName)
      {
         return (200);
      }

      switch (field.type)
      {
         case QFieldType.DECIMAL:
         case QFieldType.INTEGER:

            if (table && field.name === table.primaryKeyField && field.label.length < 3)
            {
               return (75);
            }

            return (100);
         case QFieldType.DATE:
            return (100);
         case QFieldType.DATE_TIME:
            return (200);
         case QFieldType.BOOLEAN:
            return (75);
      }

      return (200);
   };
}

